import {
  Alert,
  Box,
  Button,
  Fade,
  InputAdornment,
  TextField,
  Typography,
  useTheme,
} from "@mui/material";
import React, { useState } from "react";
import LucideIcon from "../../../../components/LucideIcon";
import { validateURL, withProtocol } from "../../../../utils";
import { GuidedIntake, OnboardingTrack } from "../types";

interface BusinessIntakeStepProps {
  track: OnboardingTrack;
  intake: GuidedIntake;
  updateIntake: (field: keyof GuidedIntake, value: string) => void;
  onNext: () => void;
  onBack: () => void;
}

// The shared validateURL accepts a host with no TLD ("parallellabsapp"). Require a real
// dot-separated TLD of 2+ letters on the hostname so a mistyped domain is caught here rather
// than failing silently when we try to scrape it. Tolerates a missing protocol and any path.
const hasValidTld = (url: string): boolean => {
  const host = url
    .trim()
    .replace(/^https?:\/\//i, "")
    .split(/[/?#]/)[0]
    .split(":")[0];
  return /\.[a-z]{2,}$/i.test(host);
};

export default function BusinessIntakeStep({
  track,
  intake,
  updateIntake,
  onNext,
  onBack,
}: BusinessIntakeStepProps) {
  const [errors, setErrors] = useState<Record<string, string>>({});
  const isBusiness = track === "business";
  const theme = useTheme();

  // Leading icon for a field, colored red when that field has an error.
  // Uses theme palette values (this app uses classic createTheme, so MUI's
  // --mui-palette-* CSS variables are not available).
  const fieldIcon = (icon: string, hasError: boolean) => ({
    startAdornment: (
      <InputAdornment position="start">
        <LucideIcon
          name={icon}
          size={20}
          color={
            hasError
              ? theme.palette.error.main
              : theme.palette.action.active
          }
        />
      </InputAdornment>
    ),
  });

  const validate = (website = intake.website) => {
    const e: Record<string, string> = {};
    // Full name is required on both paths so we can address the user and save their profile.
    if (!intake.fullName.trim()) e.fullName = "Your name is required";
    if (isBusiness) {
      if (!intake.companyName.trim()) e.companyName = "Company name is required";
      if (!website.trim()) {
        e.website = "Website URL is required";
      } else if (!validateURL(website) || !hasValidTld(website)) {
        // handleNext has already prepended a protocol via withProtocol, so a failure
        // here means the URL itself is malformed. validateURL is lenient about a missing
        // TLD (it accepts "parallellabsapp"), so we additionally require a real domain.tld
        // — otherwise the typo'd host won't resolve and the site analysis silently no-ops.
        e.website = "Enter a valid website URL (e.g. example.com)";
      }
    } else {
      if (!intake.businessName.trim())
        e.businessName = "A business / working name is required";
      if (!intake.whatToStart.trim())
        e.whatToStart = "Tell us what business you want to start";
      if (!intake.idealCustomers.trim())
        e.idealCustomers = "Tell us who your ideal customers are";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleNext = () => {
    // Fix a missing protocol for the user, then validate the fixed value.
    const fixedWebsite = withProtocol(intake.website);
    if (isBusiness && fixedWebsite !== intake.website) {
      updateIntake("website", fixedWebsite);
    }
    if (validate(fixedWebsite)) onNext();
  };

  return (
    <Fade in timeout={600}>
      <Box>
        <Typography variant="h4" sx={{ fontWeight: 600, mb: 2, color: "text.primary" }}>
          {isBusiness ? "Tell us about your business" : "Tell us about your business idea"}
        </Typography>
        <Typography variant="body1" color="text.secondary" sx={{ mb: 4, lineHeight: 1.6 }}>
          {isBusiness
            ? "We'll analyze your site to personalize your experience."
            : "Be specific — we'll use this to personalize everything."}
        </Typography>

        <Box component="form" sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          {/* Full name is collected on both tracks. */}
          <TextField
            label="Full name"
            value={intake.fullName}
            onChange={(e) => updateIntake("fullName", e.target.value)}
            error={!!errors.fullName}
            helperText={errors.fullName}
            fullWidth
            required
            InputProps={fieldIcon("User", !!errors.fullName)}
          />

          {isBusiness ? (
            <>
              <TextField
                label="Company name"
                value={intake.companyName}
                onChange={(e) => updateIntake("companyName", e.target.value)}
                error={!!errors.companyName}
                helperText={errors.companyName}
                fullWidth
                required
                InputProps={fieldIcon("Building2", !!errors.companyName)}
              />
              <TextField
                label="Company website"
                value={intake.website}
                onChange={(e) => updateIntake("website", e.target.value.trim())}
                error={!!errors.website}
                helperText={errors.website || "We'll analyze your site to personalize your experience"}
                fullWidth
                required
                placeholder="https://example.com"
                InputProps={fieldIcon("Globe", !!errors.website)}
              />
            </>
          ) : (
            <>
              <TextField
                label="Business name / working name"
                value={intake.businessName}
                onChange={(e) => updateIntake("businessName", e.target.value)}
                error={!!errors.businessName}
                helperText={errors.businessName}
                fullWidth
                required
                InputProps={fieldIcon("Building2", !!errors.businessName)}
              />
              <TextField
                label="What business do you want to start?"
                value={intake.whatToStart}
                onChange={(e) => updateIntake("whatToStart", e.target.value)}
                error={!!errors.whatToStart}
                helperText={errors.whatToStart}
                fullWidth
                required
                multiline
                minRows={2}
              />
              <TextField
                label="Who are your ideal customers?"
                value={intake.idealCustomers}
                onChange={(e) => updateIntake("idealCustomers", e.target.value)}
                error={!!errors.idealCustomers}
                helperText={errors.idealCustomers}
                fullWidth
                required
                multiline
                minRows={2}
              />
            </>
          )}

          {Object.keys(errors).length > 0 && (
            <Alert severity="error">Please complete the required fields to continue.</Alert>
          )}

          <Box sx={{ display: "flex", gap: 2, mt: 1 }}>
            <Button variant="outlined" onClick={onBack} sx={{ px: 3 }}>
              Back
            </Button>
            <Button
              variant="contained"
              onClick={handleNext}
              sx={{ px: 4, fontWeight: 600, textTransform: "none" }}
            >
              Let's go!
            </Button>
          </Box>
        </Box>
      </Box>
    </Fade>
  );
}
